import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

/**
 * Shared navigation component for all public pages
 * - Consistent branding and links across LandingPage, Pricing, Login, Register, etc.
 * - Mobile hamburger menu for small screens
 */
function PublicNav() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { to: '/pricing', label: 'Pricing' },
    { to: '/about', label: 'About' },
    { to: '/contact', label: 'Contact' },
  ];

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      <nav className="public-nav">
        <div className="public-nav-container">
          {/* Logo */}
          <Link to="/" className="public-nav-logo" onClick={closeMobileMenu}>
            <span className="logo-icon">🌿</span>
            <span className="logo-text">LawnTrack</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="public-nav-links desktop-only">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`public-nav-link ${isActive(link.to) ? 'active' : ''}`}
              >
                {link.label}
              </Link>
            ))}
            <Link to="/login" className="public-nav-link">
              Login
            </Link>
            <Link to="/register" className="public-nav-cta">
              Get Started
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="mobile-menu-btn mobile-only"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            <span className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`mobile-menu-link ${isActive(link.to) ? 'active' : ''}`}
              onClick={closeMobileMenu}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/login"
            className="mobile-menu-link"
            onClick={closeMobileMenu}
          >
            Login
          </Link>
          <Link
            to="/register"
            className="mobile-menu-cta"
            onClick={closeMobileMenu}
          >
            Get Started Free
          </Link>
        </div>
      </nav>

      {/* Overlay for mobile menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={closeMobileMenu} />
      )}

      <style>{`
        /* Public Navigation */
        .public-nav {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(10px);
          z-index: 1000;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .public-nav-container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 24px;
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        /* Logo */
        .public-nav-logo {
          display: flex;
          align-items: center;
          gap: 8px;
          text-decoration: none;
          font-weight: 700;
          font-size: 1.25rem;
        }

        .logo-icon {
          font-size: 1.5rem;
        }

        .logo-text {
          color: #4CAF50;
        }

        /* Desktop Nav Links */
        .public-nav-links {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .public-nav-link {
          color: #374151;
          text-decoration: none;
          font-weight: 500;
          padding: 8px 16px;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .public-nav-link:hover {
          background: #f3f4f6;
          color: #111827;
        }

        .public-nav-link.active {
          color: #4CAF50;
          background: rgba(76, 175, 80, 0.1);
        }

        .public-nav-cta {
          background: #4CAF50;
          color: white;
          text-decoration: none;
          font-weight: 600;
          padding: 10px 20px;
          border-radius: 8px;
          margin-left: 8px;
          transition: all 0.2s ease;
        }

        .public-nav-cta:hover {
          background: #388E3C;
          transform: translateY(-1px);
        }

        /* Mobile Menu Button */
        .mobile-menu-btn {
          display: none;
          background: none;
          border: none;
          padding: 8px;
          cursor: pointer;
          z-index: 1001;
        }

        .hamburger {
          display: flex;
          flex-direction: column;
          gap: 5px;
          width: 24px;
        }

        .hamburger span {
          display: block;
          height: 2px;
          background: #374151;
          border-radius: 2px;
          transition: all 0.3s ease;
        }

        .hamburger.open span:nth-child(1) {
          transform: rotate(45deg) translate(5px, 5px);
        }

        .hamburger.open span:nth-child(2) {
          opacity: 0;
        }

        .hamburger.open span:nth-child(3) {
          transform: rotate(-45deg) translate(5px, -5px);
        }

        /* Mobile Menu */
        .mobile-menu {
          display: none;
          position: fixed;
          top: 64px;
          left: 0;
          right: 0;
          background: white;
          padding: 16px 24px 24px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          transform: translateY(-100%);
          opacity: 0;
          transition: all 0.3s ease;
          z-index: 999;
        }

        .mobile-menu.open {
          transform: translateY(0);
          opacity: 1;
        }

        .mobile-menu-link {
          display: block;
          color: #374151;
          text-decoration: none;
          font-weight: 500;
          padding: 14px 0;
          border-bottom: 1px solid #f3f4f6;
          transition: color 0.2s ease;
        }

        .mobile-menu-link:hover,
        .mobile-menu-link.active {
          color: #4CAF50;
        }

        .mobile-menu-cta {
          display: block;
          background: #4CAF50;
          color: white;
          text-decoration: none;
          font-weight: 600;
          padding: 14px;
          border-radius: 8px;
          text-align: center;
          margin-top: 16px;
        }

        .mobile-menu-cta:hover {
          background: #388E3C;
        }

        .mobile-menu-overlay {
          display: none;
          position: fixed;
          top: 64px;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.3);
          z-index: 998;
        }

        /* Responsive */
        .desktop-only {
          display: flex;
        }

        .mobile-only {
          display: none;
        }

        @media (max-width: 768px) {
          .public-nav-container {
            padding: 0 16px;
            height: 56px;
          }

          .public-nav-logo {
            font-size: 1.1rem;
          }

          .logo-icon {
            font-size: 1.3rem;
          }

          .desktop-only {
            display: none;
          }

          .mobile-only {
            display: block;
          }

          .mobile-menu {
            display: block;
          }

          .mobile-menu-overlay {
            display: block;
          }

          .mobile-menu.open ~ .mobile-menu-overlay {
            display: block;
          }
        }

        /* Space for fixed nav */
        .public-nav + * {
          padding-top: 64px;
        }

        @media (max-width: 768px) {
          .public-nav + * {
            padding-top: 56px;
          }
        }
      `}</style>
    </>
  );
}

export default PublicNav;
